import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { H2 } from "components/ui/typography/titles/Titles"
import { TableQuotations } from "./layout/TableQuotations/TableQuotations"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useCreateQuotation, useGetQuotations } from "hooks/api/quotation/quotations.hook"
import quotationsService from "services/quotations.service"
import { useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { QuotationViewerModal } from "components/ui/customWebviewer/QuotationViewerModal"
import type { TQuotationViewerMode } from "components/ui/customWebviewer/QuotationViewerModal"
import { useDocumentDownload } from "hooks/api/downloadFIle/documentDownload.hook"
import { useDocuments } from "hooks/api/drawings/drawings.hook"
import { usePresignedDownloadUrl } from "hooks/api/downloadFIle/downloadFIle.hook"
import { removeApiVersion } from "helpers/utils.helper"
import { CreateQuotationModal } from "./layout/CreateQuotationModal/CreateQuotationModal"
import { RenameQuotationModal } from "./layout/RenameQuotationModal/RenameQuotationModal"
import { QuotationEditorDialogs } from "components/ui/customWebviewer/QuotationEditorDialogs/QuotationEditorDialogs"
import { ProjectNumberGateModal } from "./layout/ProjectNumberGateModal/ProjectNumberGateModal"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useProjectNumber } from "hooks/useProjectNumber.hook"
import { useMessage } from "hooks/message.hook"
import { isMissingProjectNumberError } from "helpers/projectNumber.helper"
import { ProjectNumber } from "components/ui/ProjectNumber/ProjectNumber"
import { useViewerStore } from "store/viewer.store"
import type { TDrawingStatus } from "types/drawer.type"
import style from "./Quotations.module.scss"

const {
	quotationsStyles,
	header,
	title,
	titleRow,
	closeButton,
	closeIcon,
	createQuotationButton,
	buttonWrapper,
	tableWrapper,
} = style

type Props = {
	onCloseDrawer: () => void
	drawingName: string
	drawingStatus: TDrawingStatus
	fileOwner: "me" | "other"
}

export const Quotations = ({ onCloseDrawer, drawingName, drawingStatus, fileOwner }: Props) => {
	const { downloadHandle } = useDocumentDownload()
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { closeQuotationFeedback, closeQuotationSettings } = useViewerStore()

	const [searchParams, setSearchParams] = useSearchParams()

	const [isCreateQuotationModalOpen, setCreateQuotationModalOpen] = useState(false)
	const [isProjectNumberGateOpen, setProjectNumberGateOpen] = useState(false)
	// Single shared Apryse viewer: "preview" (read-only) and "editor" reuse ONE instance (#1119).
	const [viewerMode, setViewerMode] = useState<TQuotationViewerMode>("closed")
	const [loadingQuotationId, setLoadingQuotationId] = useState<number | null>(null)
	const editRequestRef = useRef(0)
	const isMountedRef = useRef(true)

	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
		}
	}, [])

	// Close any open feedback/settings modal when this tab unmounts so neither leaks into other tabs
	// (the flags are global, so without this a stale-open flag would re-open an orphan modal on remount).
	// The viewer modal is driven by local `viewerMode` state, so it tears down with the component.
	useEffect(() => {
		return () => {
			closeQuotationFeedback()
			closeQuotationSettings()
		}
	}, [closeQuotationFeedback, closeQuotationSettings])

	const [renameQuotationState, setRenameQuotationState] = useState<{
		isOpen: boolean
		quotationId: number | null
		quotationName: string
	}>({
		isOpen: false,
		quotationId: null,
		quotationName: "",
	})

	const drawingId = Number(searchParams.get("drawingID"))
	const processingId = Number(searchParams.get("processingID"))

	const { data: quotations } = useGetQuotations(drawingId)
	const isNoQuotations = quotations?.length === 0
	const userIsNotFileOwner = fileOwner === "other"

	// Create-quotation gate (REQ-377): every creation path (new + duplicate) goes through one
	// project-number check. If unset, stash the intended action and open the gate, which resumes it.
	const { isSet: isProjectNumberSet } = useProjectNumber(drawingId)
	const { mutate: createQuotationMutation } = useCreateQuotation()
	const { success: successMessage } = useMessage()
	const [pendingCreateAction, setPendingCreateAction] = useState<(() => void) | null>(null)

	const promptProjectNumber = (retry: () => void) => {
		setPendingCreateAction(() => retry)
		setProjectNumberGateOpen(true)
	}

	const guardWithProjectNumber = (action: () => void) => {
		if (!isProjectNumberSet) {
			promptProjectNumber(action)
			return
		}
		action()
	}

	const duplicateQuotation = (quotationId: number) => {
		const url = window.location.href
		const sourceUrl = new URL(url)
		sourceUrl.searchParams.set("quotationID", String(quotationId))

		createQuotationMutation(
			{
				drawing_id: drawingId,
				source_entity_id: quotationId,
				source_entity_type: "quotation",
				source_url: sourceUrl.toString(),
				processing_url: url,
			},
			{
				onSuccess: () => {
					successMessage("Quotation created successfully")
					queryClient.invalidateQueries({ queryKey: ["quotations", drawingId] })
				},
				onError: (error) => {
					// Safety net: if the backend still rejects for a missing project number, surface the gate
					// (resuming this duplicate) rather than a raw toast.
					if (isMissingProjectNumberError(error)) {
						promptProjectNumber(() => duplicateQuotation(quotationId))
						return
					}
					formatError(error)
				},
			},
		)
	}

	const handleCreateQuotation = () => guardWithProjectNumber(() => setCreateQuotationModalOpen(true))
	const handleDuplicateQuotation = (quotationId: number) => guardWithProjectNumber(() => duplicateQuotation(quotationId))

	const { data: documentsData } = useDocuments(drawingId, processingId, drawingStatus)
	const originalDocEndpoint = useMemo(() => {
		const originalDoc = documentsData?.documents?.find((doc: { note: string }) => doc.note === "Original")
		return originalDoc ? removeApiVersion(originalDoc.download_endpoint) : null
	}, [documentsData])
	const { data: originalPdfData } = usePresignedDownloadUrl(originalDocEndpoint)

	const processingUrl = window.location.href
	// Closing the viewer also hides the feedback modal, but the FeedbackPanel stays mounted
	// (see below) so any in-progress draft survives until the feedback is actually sent.
	// The settings modal is also dismissed so it can't linger over a closed viewer.
	const closeViewer = useCallback(() => {
		setViewerMode("closed")
		closeQuotationFeedback()
		closeQuotationSettings()
		queryClient.invalidateQueries({ queryKey: ["quotations", drawingId] })
	}, [closeQuotationFeedback, closeQuotationSettings, drawingId, queryClient])

	const handleEditQuotation = async (quotationId: number) => {
		if (loadingQuotationId !== null) return
		const requestId = ++editRequestRef.current
		setLoadingQuotationId(quotationId)
		try {
			await queryClient.ensureQueryData({
				queryKey: ["quotation", quotationId],
				queryFn: () => quotationsService.getQuotation(quotationId),
			})
		} catch (error) {
			if (isMountedRef.current && editRequestRef.current === requestId) {
				setLoadingQuotationId(null)
			}
			formatError(error)
			return
		}
		if (!isMountedRef.current || editRequestRef.current !== requestId) return
		// Set quotationID in the URL so the row unfolds (collapsing any other expanded row) and
		// every customWebviewer reader (sidebars, save-hooks) picks up the right id. Then open
		// the editor — data is already prefetched into the React Query cache above.
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.set("quotationID", String(quotationId))
				return next
			},
			{ replace: true },
		)
		setLoadingQuotationId(null)
		setViewerMode("editor")
	}

	return (
		<div className={quotationsStyles}>
			<div className={header}>
				<div className={titleRow}>
					<H2 className={title}>{drawingName}</H2>
					<ProjectNumber editable={fileOwner === "me"} />
				</div>
				<button className={closeButton} onClick={onCloseDrawer} aria-label="Close quotations drawer">
					<CloseIcon className={closeIcon} />
				</button>
			</div>
			<div className={tableWrapper}>
				<TableQuotations
					userIsNotFileOwner={userIsNotFileOwner}
					onDownload={downloadHandle}
					onPreview={() => setViewerMode("preview")}
					createQuotation={handleCreateQuotation}
					duplicateQuotation={handleDuplicateQuotation}
					renameQuotation={(quotationId: number) => {
						const quotation = quotations?.find((q) => q.id === quotationId)
						setRenameQuotationState({
							isOpen: true,
							quotationId,
							quotationName: quotation?.name || "",
						})
					}}
					editQuotation={handleEditQuotation}
					loadingQuotationId={loadingQuotationId}
					isNoQuotations={isNoQuotations}
				/>
			</div>
			{!isNoQuotations && !userIsNotFileOwner && (
				<div className={buttonWrapper}>
					<ButtonPrimary onClick={handleCreateQuotation} className={createQuotationButton}>
						Create quotation
					</ButtonPrimary>
				</div>
			)}
			<CreateQuotationModal
				isModalOpen={isCreateQuotationModalOpen}
				setModalClose={() => setCreateQuotationModalOpen(false)}
				onMissingProjectNumber={() => {
					setCreateQuotationModalOpen(false)
					promptProjectNumber(() => setCreateQuotationModalOpen(true))
				}}
			/>
			<ProjectNumberGateModal
				isOpen={isProjectNumberGateOpen}
				onClose={() => {
					setPendingCreateAction(null)
					setProjectNumberGateOpen(false)
				}}
				onContinue={() => {
					setProjectNumberGateOpen(false)
					const action = pendingCreateAction
					setPendingCreateAction(null)
					action?.()
				}}
			/>
			<RenameQuotationModal
				isModalOpen={renameQuotationState.isOpen}
				setModalClose={() => setRenameQuotationState({ isOpen: false, quotationId: null, quotationName: "" })}
				quotationId={renameQuotationState.quotationId}
				quotationName={renameQuotationState.quotationName}
			/>
			<QuotationViewerModal
				mode={viewerMode}
				onClose={closeViewer}
				fileLink={originalPdfData?.download_url}
				drawingId={drawingId}
				processingId={processingId}
				processingUrl={processingUrl}
				drawingStatus={drawingStatus}
			/>
			<QuotationEditorDialogs
				drawingId={drawingId}
				processingId={Number.isFinite(processingId) ? processingId : null}
				quotationId={Number(searchParams.get("quotationID")) || undefined}
				processingUrl={processingUrl}
				drawingStatus={drawingStatus}
			/>
		</div>
	)
}
